/**
 * Centralized logging utility for KBros Leather
 * Provides environment-aware logging with different levels
 */

const LOG_LEVELS = {
  ERROR: 0,
  WARN: 1,
  INFO: 2,
  DEBUG: 3
};

class Logger {
  constructor() {
    // Set log level based on environment
    this.logLevel = this.getLogLevel();
    this.isDevelopment = process.env.NODE_ENV === 'development';
    this.isProduction = process.env.NODE_ENV === 'production';
  }

  getLogLevel() {
    const envLevel = process.env.LOG_LEVEL?.toLowerCase();
    switch (envLevel) {
      case 'error': return LOG_LEVELS.ERROR;
      case 'warn': return LOG_LEVELS.WARN;
      case 'info': return LOG_LEVELS.INFO;
      case 'debug': return LOG_LEVELS.DEBUG;
      default: 
        // Default to INFO in production, DEBUG in development
        return process.env.NODE_ENV === 'production' ? LOG_LEVELS.INFO : LOG_LEVELS.DEBUG;
    }
  }

  shouldLog(level) {
    return level <= this.logLevel;
  }

  formatMessage(level, message, context = {}) {
    const timestamp = new Date().toISOString();
    const levelStr = Object.keys(LOG_LEVELS)[level];
    
    if (this.isDevelopment) {
      // Rich formatting for development
      return {
        timestamp,
        level: levelStr,
        message,
        context
      };
    } else {
      // Structured logging for production
      return JSON.stringify({
        timestamp,
        level: levelStr,
        message,
        ...context
      });
    }
  }

  error(message, context = {}) {
    if (this.shouldLog(LOG_LEVELS.ERROR)) {
      if (this.isDevelopment) {
        console.error('🔴 ERROR:', message, context);
      } else {
        console.error(this.formatMessage(LOG_LEVELS.ERROR, message, context));
      }
    }
  }

  warn(message, context = {}) {
    if (this.shouldLog(LOG_LEVELS.WARN)) {
      if (this.isDevelopment) {
        console.warn('🟡 WARN:', message, context);
      } else {
        console.warn(this.formatMessage(LOG_LEVELS.WARN, message, context));
      }
    }
  }

  info(message, context = {}) {
    if (this.shouldLog(LOG_LEVELS.INFO)) {
      if (this.isDevelopment) {
        console.info('🔵 INFO:', message, context);
      } else {
        console.info(this.formatMessage(LOG_LEVELS.INFO, message, context));
      }
    }
  }

  debug(message, context = {}) {
    if (this.shouldLog(LOG_LEVELS.DEBUG)) {
      if (this.isDevelopment) {
        console.debug('🟣 DEBUG:', message, context);
      } else {
        console.debug(this.formatMessage(LOG_LEVELS.DEBUG, message, context));
      }
    }
  }

  // Cart-specific logging
  cartAction(action, payload) {
    this.debug('Cart Action', { action, payload });
  }

  // API-specific logging
  apiRequest(method, url, data = {}) {
    this.debug('API Request', { method, url, data });
  }

  apiResponse(method, url, status, data = {}) {
    if (status >= 400) {
      this.warn('API Error Response', { method, url, status, data });
    } else {
      this.debug('API Response', { method, url, status });
    }
  }

  // Auth-specific logging
  authEvent(event, context = {}) {
    this.info('Auth Event', { event, ...context });
  }

  // Performance logging
  performance(operation, duration, context = {}) {
    if (duration > 1000) {
      this.warn('Slow Operation', { operation, duration, ...context });
    } else {
      this.debug('Performance', { operation, duration, ...context });
    }
  }
}

// Create singleton instance
const logger = new Logger();

export default logger;